import type { CSSProperties } from "react";
import { localizeByKeyStatic } from "components/service";
import CommunicationConst from "../../communication-const";
import { localizeCommunicationLookupValue } from "../../helpers/communication-localize-helper";
import {
  formatCommunicationStandGroupTitle,
  formatCommunicationStandNumberLabel,
} from "../../helpers/communication-stand-format-helper";
import type {
  TCommunicationContextGroup,
  TCommunicationEquipmentChange,
  TCommunicationReferenceOption,
  TCommunicationRequestAction,
  TCommunicationRequestItem,
  TCommunicationRequestGroup,
  TCommunicationRequestSource,
  TCommunicationRequestStatus,
  TCommunicationSuggestionStatus,
  TCommunicationTask,
  TCommunicationTaskFormValues,
} from "../../helpers/communication-types-helper";
import type { TCommunicationTaskStep } from "../../helpers/communication-step-helper";

const COMMUNICATION_DRAWER_BREAKPOINTS = {
  mobile: 767,
  tablet: 1199,
  laptop: 1519,
} as const;

export type TCommunicationDrawerPresentationMode =
  | "desktop"
  | "laptop-condensed"
  | "tablet-condensed"
  | "mobile-simplified";

export type TValidationFieldName = string | (string | number)[];
export type TValidateFieldsError = {
  errorFields?: Array<{
    name: (string | number)[];
  }>;
};

export type TEditableChangeGroup = {
  key: string;
  standNumber?: string;
  eventName?: string;
  companyName?: string;
  itemIndexes: number[];
};

type TEquipmentSuggestionMeta = {
  suggestedGroupId: string;
  status: TCommunicationSuggestionStatus;
};

export type TCommunicationRequestDisplayMode = "ai_fallback" | "structured";
export type TCommunicationRequestDisplayStatus =
  | "matched_pending"
  | "no_catalog_match"
  | "approved"
  | "rejected"
  | "cancelled";

export const getCommunicationDrawerPresentationMode = (
  viewportWidth: number
): TCommunicationDrawerPresentationMode => {
  if (viewportWidth > 0 && viewportWidth <= COMMUNICATION_DRAWER_BREAKPOINTS.mobile) {
    return "mobile-simplified";
  }

  if (viewportWidth > 0 && viewportWidth <= COMMUNICATION_DRAWER_BREAKPOINTS.tablet) {
    return "tablet-condensed";
  }

  if (viewportWidth > 0 && viewportWidth <= COMMUNICATION_DRAWER_BREAKPOINTS.laptop) {
    return "laptop-condensed";
  }

  return "desktop";
};

export const getDefaultReadingPaneExpanded = (
  mode: TCommunicationDrawerPresentationMode,
  step: number
): boolean => {
  if (mode === "mobile-simplified") {
    return false;
  }

  if (mode === "tablet-condensed" || mode === "laptop-condensed") {
    return step === 1;
  }

  return step === 1;
};

export const createEmptyEquipmentChange = (taskId: string, standNumber?: string) => ({
  id: `new-${taskId}-${Date.now()}`,
  action: "add" as const,
  item_label: "",
  event_name: undefined,
  company_name: undefined,
  stand_number: standNumber,
  qty: 1,
  color: null,
  equipment_group_id: undefined,
  suggested_equipment_group_id: undefined,
  suggestion_status: undefined,
});

export const buildContextGroupLookupKey = (
  eventName?: string | null,
  companyName?: string | null,
  standNumber?: string | null
): string => [ eventName?.trim() || "", companyName?.trim() || "", standNumber?.trim() || "" ].join("|");

export const findMatchingContextGroup = (
  change: Partial<TCommunicationEquipmentChange> | null | undefined,
  contextGroups: TCommunicationContextGroup[]
): TCommunicationContextGroup | undefined => {
  const eventName = typeof change?.event_name === "string" ? change.event_name.trim() : "";
  const companyName = typeof change?.company_name === "string" ? change.company_name.trim() : "";
  const standNumber = typeof change?.stand_number === "string" ? change.stand_number.trim() : "";

  return contextGroups.find((group) =>
    group.event_name.trim() === eventName
    && group.company_name.trim() === companyName
    && group.stand_number.trim() === standNumber
  )
    || contextGroups.find((group) =>
      group.stand_number.trim() === standNumber
      && (!eventName || group.event_name.trim() === eventName)
      && (!companyName || group.company_name.trim() === companyName)
    )
    || contextGroups.find((group) => group.stand_number.trim() === standNumber);
};

export const isEquipmentChangeVisibleForContexts = (
  change: Partial<TCommunicationEquipmentChange> | null | undefined,
  contextGroups: TCommunicationContextGroup[]
): boolean => {
  if (!contextGroups.length) {
    return true;
  }

  const hasExplicitContext = Boolean(
    (typeof change?.event_name === "string" && change.event_name.trim())
    || (typeof change?.company_name === "string" && change.company_name.trim())
    || (typeof change?.stand_number === "string" && change.stand_number.trim())
  );

  if (!hasExplicitContext) {
    return contextGroups.length <= 1;
  }

  return Boolean(findMatchingContextGroup(change, contextGroups));
};

export const getExcludedContextGroupsFromRequestGroups = (
  requestGroups: TCommunicationRequestGroup[],
  contextGroups: TCommunicationContextGroup[]
): TCommunicationContextGroup[] => {
  const activeContextKeys = new Set(
    contextGroups.map((group) =>
      buildContextGroupLookupKey(group.event_name, group.company_name, group.stand_number)
    )
  );

  return requestGroups
    .map((group) => ({
      id: group.id,
      event_name: group.event_name,
      company_name: group.company_name,
      stand_number: group.stand_number,
    }))
    .filter((group) =>
      !activeContextKeys.has(buildContextGroupLookupKey(group.event_name, group.company_name, group.stand_number))
    );
};

export const stripStepTitleIndex = (label: string): string =>
  label.replace(/^\s*\d+\.\s*/u, "").trim();

export const normalizeStandNumbers = (values: Array<string | null | undefined>): string[] =>
  Array.from(
    new Set(
      values
        .map((value) => (typeof value === "string" ? value.trim() : ""))
        .filter(Boolean)
    )
  );

export const normalizeContextGroups = (
  contextGroups: Array<Partial<TCommunicationContextGroup> | null | undefined>,
  taskId: string
): TCommunicationContextGroup[] => {
  const normalizedGroups = contextGroups
    .map((group, index) => ({
      id:
        typeof group?.id === "string" && group.id.trim()
          ? group.id.trim()
          : `context-${taskId}-${index}`,
      event_id: typeof group?.event_id === "string" ? group.event_id : undefined,
      event_name: typeof group?.event_name === "string" ? group.event_name : "",
      company_id: typeof group?.company_id === "string" ? group.company_id : undefined,
      company_name: typeof group?.company_name === "string" ? group.company_name : "",
      stand_id: typeof group?.stand_id === "string" ? group.stand_id : undefined,
      cad_id: typeof group?.cad_id === "string" ? group.cad_id : undefined,
      stand_drawing_preview_url:
        typeof group?.stand_drawing_preview_url === "string"
          ? group.stand_drawing_preview_url
          : undefined,
      stand_drawing_file_path:
        typeof group?.stand_drawing_file_path === "string"
          ? group.stand_drawing_file_path
          : undefined,
      stand_number: typeof group?.stand_number === "string" ? group.stand_number : "",
    }))
    .filter((group) => group.event_name.trim() || group.company_name.trim() || group.stand_number.trim());

  return normalizedGroups.length > 0
    ? normalizedGroups
    : [{
      id: `context-${taskId}-0`,
      event_id: undefined,
      event_name: "",
      company_id: undefined,
      company_name: "",
      stand_id: undefined,
      cad_id: undefined,
      stand_drawing_preview_url: undefined,
      stand_drawing_file_path: undefined,
      stand_number: "",
    }];
};

export const getPrimaryContextGroup = (
  contextGroups: TCommunicationContextGroup[]
): TCommunicationContextGroup | undefined =>
  contextGroups.find((group) =>
    group.event_name.trim() && group.company_name.trim() && group.stand_number.trim()
  ) || contextGroups[0];

export const areContextGroupsComplete = (
  contextGroups: TCommunicationContextGroup[]
): boolean =>
  contextGroups.length > 0
  && contextGroups.every((group) =>
    Boolean(group.event_name.trim() && group.company_name.trim() && group.stand_number.trim())
  );

export const formatStandGroupTitle = (value?: string | null): string =>
  formatCommunicationStandGroupTitle(
    value,
    localizeByKeyStatic("COMMUNICATION.RequestGroup_StandFallback")
  );

export const buildStandToneStyle = (index: number): CSSProperties => {
  const mutedStandPalette = [
    {
      accent: "hsl(212 29% 50%)",
      soft: "hsl(212 32% 86% / 0.16)",
      pillBg: "hsl(212 34% 96%)",
      pillBorder: "hsl(212 22% 80%)",
      pillColor: "hsl(212 26% 41%)",
    },
    {
      accent: "hsl(148 17% 44%)",
      soft: "hsl(148 18% 84% / 0.16)",
      pillBg: "hsl(148 22% 96%)",
      pillBorder: "hsl(148 14% 80%)",
      pillColor: "hsl(148 18% 36%)",
    },
    {
      accent: "hsl(31 27% 52%)",
      soft: "hsl(31 30% 84% / 0.16)",
      pillBg: "hsl(31 32% 96%)",
      pillBorder: "hsl(31 22% 80%)",
      pillColor: "hsl(31 28% 39%)",
    },
    {
      accent: "hsl(311 14% 49%)",
      soft: "hsl(311 16% 84% / 0.16)",
      pillBg: "hsl(311 20% 96%)",
      pillBorder: "hsl(311 12% 80%)",
      pillColor: "hsl(311 14% 38%)",
    },
    {
      accent: "hsl(191 17% 45%)",
      soft: "hsl(191 18% 84% / 0.16)",
      pillBg: "hsl(191 20% 96%)",
      pillBorder: "hsl(191 14% 80%)",
      pillColor: "hsl(191 18% 36%)",
    },
    {
      accent: "hsl(9 20% 52%)",
      soft: "hsl(9 22% 84% / 0.16)",
      pillBg: "hsl(9 24% 96%)",
      pillBorder: "hsl(9 16% 80%)",
      pillColor: "hsl(9 18% 39%)",
    },
  ];
  const tone = mutedStandPalette[index % mutedStandPalette.length];

  return {
    "--communication-stand-accent": tone.accent,
    "--communication-stand-soft": tone.soft,
    "--communication-stand-pill-bg": tone.pillBg,
    "--communication-stand-pill-border": tone.pillBorder,
    "--communication-stand-pill-color": tone.pillColor,
  } as CSSProperties;
};

export const buildNeutralStandToneStyle = (): CSSProperties =>
  ({
    "--communication-stand-accent": "hsl(214 22% 58%)",
    "--communication-stand-soft": "hsl(214 26% 92% / 0.2)",
    "--communication-stand-pill-bg": "hsl(214 50% 96%)",
    "--communication-stand-pill-border": "hsl(214 34% 84%)",
    "--communication-stand-pill-color": "hsl(214 34% 42%)",
  }) as CSSProperties;

export const formatCanonicalStandNumberLabel = formatCommunicationStandNumberLabel;

export const getEditableChangeStandNumber = (
  change?: TCommunicationEquipmentChange | null
): string => (typeof change?.stand_number === "string" ? change.stand_number.trim() : "");

export const hasStandUpdates = (
  standUpdates?: Partial<TCommunicationTaskFormValues["stand_updates"]> | null
): boolean =>
  Boolean(
    (typeof standUpdates?.flooring_id === "string"
      ? standUpdates.flooring_id.trim()
      : standUpdates?.flooring_id)
    || (typeof standUpdates?.flooring_color === "string"
      ? standUpdates.flooring_color.trim()
      : standUpdates?.flooring_color)
  );

export const hasManagerNote = (managerNote?: string | null): boolean =>
  Boolean(managerNote?.trim());

export const resolveEquipmentGroupId = (
  equipmentGroupId: string | null | undefined,
  equipmentId: string | null | undefined,
  equipmentIdToGroupIdMap: Map<string, string>
): string | undefined => {
  if (equipmentGroupId?.trim()) {
    return equipmentGroupId;
  }

  if (equipmentId?.trim()) {
    return equipmentIdToGroupIdMap.get(equipmentId);
  }

  return undefined;
};

export const buildMergedEquipmentOptions = (
  equipmentOptions: TCommunicationReferenceOption[],
  equipmentChanges: TCommunicationTask["final_changes"]["equipment_changes"],
  equipmentIdToGroupIdMap: Map<string, string>
): TCommunicationReferenceOption[] => {
  const optionMap = new Map(
    equipmentOptions.map((option) => [ option.value, option ])
  );

  equipmentChanges.forEach((change) => {
    const resolvedGroupId = resolveEquipmentGroupId(
      change.equipment_group_id || change.suggested_equipment_group_id,
      change.equipment_id,
      equipmentIdToGroupIdMap
    );

    if (!resolvedGroupId || optionMap.has(resolvedGroupId)) {
      return;
    }

    optionMap.set(resolvedGroupId, {
      value: resolvedGroupId,
      label: change.equipment_title || change.item_label || resolvedGroupId,
      rawId: change.equipment_id,
    });
  });

  return Array.from(optionMap.values());
};

export const getEquipmentSuggestionMeta = (
  change: TCommunicationTaskFormValues["equipment_changes"][number]
): TEquipmentSuggestionMeta | null => {
  if (!change?.suggested_equipment_group_id) {
    return null;
  }

  return {
    suggestedGroupId: change.suggested_equipment_group_id,
    status: change.suggestion_status || "pending",
  };
};

export const getEffectiveEquipmentGroupId = (
  change?: TCommunicationTaskFormValues["equipment_changes"][number] | null
): string | undefined =>
  change?.equipment_group_id
  || (change?.suggestion_status !== "changed" ? change?.suggested_equipment_group_id || undefined : undefined);

export const getCommunicationRequestDisplayMode = (
  requestSource?: TCommunicationRequestSource | null
): TCommunicationRequestDisplayMode =>
  requestSource === "ai_fallback" ? "ai_fallback" : "structured";

export const isAiFallbackRequestMode = (
  mode: TCommunicationRequestDisplayMode
): boolean => mode === "ai_fallback";

export const hasCatalogMatch = (
  request?: Partial<TCommunicationRequestItem> | null
): boolean =>
  Boolean(
    request?.equipment_group_id?.trim()
    || request?.equipment_id?.trim()
    || request?.flooring_id?.trim()
  );

export const getCommunicationRequestDisplayStatus = (
  request?: Partial<TCommunicationRequestItem> | null
): TCommunicationRequestDisplayStatus => {
  const status = request?.status;

  if (status === "approved") {
    return "approved";
  }

  if (status === "rejected") {
    return "rejected";
  }

  if (status === "cancelled") {
    return "cancelled";
  }

  if (status === "unidentified" || !hasCatalogMatch(request)) {
    return "no_catalog_match";
  }

  return "matched_pending";
};

export const getCommunicationRequestDisplayStatusLabelKey = (
  displayStatus: TCommunicationRequestDisplayStatus,
  rawStatus?: TCommunicationRequestStatus | null,
  rawAction?: TCommunicationRequestAction | null
): string => {
  if (rawAction === "question") {
    if (rawStatus === "approved") {
      return "COMMUNICATION.RequestStatus_Approved";
    }

    if (rawStatus === "rejected") {
      return "COMMUNICATION.RequestStatus_Rejected";
    }

    if (rawStatus === "cancelled") {
      return "COMMUNICATION.RequestStatus_Cancelled";
    }

    return "COMMUNICATION.RequestStatus_Pending";
  }

  if (displayStatus === "matched_pending") {
    return "COMMUNICATION.RequestStatus_MatchedPending";
  }

  if (displayStatus === "no_catalog_match") {
    return "COMMUNICATION.RequestStatus_Unidentified";
  }

  if (displayStatus === "approved") {
    return "COMMUNICATION.RequestStatus_Approved";
  }

  if (displayStatus === "rejected") {
    return "COMMUNICATION.RequestStatus_Rejected";
  }

  if (displayStatus === "cancelled") {
    return "COMMUNICATION.RequestStatus_Cancelled";
  }

  return rawStatus === "pending"
    ? "COMMUNICATION.RequestStatus_Pending"
    : "COMMUNICATION.RequestStatus_Unidentified";
};

export const getCommunicationRequestDisplayStatusTone = (
  displayStatus: TCommunicationRequestDisplayStatus,
  rawStatus?: TCommunicationRequestStatus | null,
  rawAction?: TCommunicationRequestAction | null
): string => {
  if (rawAction === "question") {
    if (rawStatus === "approved") {
      return "approved";
    }

    if (rawStatus === "rejected" || rawStatus === "cancelled") {
      return "muted";
    }

    return "pending";
  }

  if (displayStatus === "approved") {
    return "approved";
  }

  if (displayStatus === "no_catalog_match") {
    return "warning";
  }

  if (displayStatus === "rejected" || displayStatus === "cancelled") {
    return "muted";
  }

  return "pending";
};

export const getCommunicationRequestConfidenceLabelKey = (
  mode: TCommunicationRequestDisplayMode
): string =>
  mode === "ai_fallback"
    ? "COMMUNICATION.RequestGroup_Confidence"
    : "COMMUNICATION.RequestGroup_RequestConfidence";

export const getCommunicationRequestNoCatalogMatchHintKey = (
  mode: TCommunicationRequestDisplayMode
): string =>
  mode === "ai_fallback"
    ? "COMMUNICATION.RequestGroup_NoCatalogMatchHint"
    : "COMMUNICATION.RequestGroup_NoCatalogMatchHintStructured";

export const isBlankEquipmentChange = (
  change?: TCommunicationTaskFormValues["equipment_changes"][number] | null
): boolean =>
  (change?.action || "add") === "add"
  && (typeof change?.qty === "number" ? change.qty : 1) === 1
  && !getEffectiveEquipmentGroupId(change)
  && !change?.equipment_id?.trim()
  && !change?.item_label?.trim()
  && !change?.equipment_title?.trim()
  && !change?.color?.trim();

export const shouldIgnoreEquipmentChangeValidation = (
  change: TCommunicationTaskFormValues["equipment_changes"][number] | undefined,
  index: number,
  changes: TCommunicationTaskFormValues["equipment_changes"],
  hasExistingEquipmentChanges: boolean
): boolean =>
  !hasExistingEquipmentChanges
  && changes.length === 1
  && index === 0
  && isBlankEquipmentChange(change);

export const getEquipmentValidationMessage = (
  change: TCommunicationTaskFormValues["equipment_changes"][number] | undefined
): string => {
  const sourceLabel =
    change?.equipment_title?.trim()
    || change?.item_label?.trim()
    || "";

  if (sourceLabel) {
    return localizeByKeyStatic("COMMUNICATION.Validation_SelectEquipmentForItemOrDelete", {
      item: sourceLabel,
    });
  }

  return localizeByKeyStatic("COMMUNICATION.Validation_SelectEquipmentOrDelete");
};

export const mapTaskToFormValues = (
  task: TCommunicationTask,
  equipmentIdToGroupIdMap: Map<string, string> = new Map()
): TCommunicationTaskFormValues => ({
  event_name: task.event_name || "",
  company_name: task.company_name || "",
  stand_number: task.stand_number || "",
  stand_numbers:
    task.stand_numbers.length > 0
      ? task.stand_numbers
      : task.stand_number
        ? [ task.stand_number ]
        : [ "" ],
  context_groups: normalizeContextGroups(
    task.context_groups?.length
      ? task.context_groups
      : [{
        id: `context-${task.id}-0`,
        event_id: task.event_id || undefined,
        event_name: task.event_name || "",
        company_id: undefined,
        company_name: task.company_name || "",
        stand_id: task.stand_id || undefined,
        stand_number: task.stand_number || task.stand_numbers[0] || "",
      }],
    task.id
  ),
  organizer_id: task.organizer_id || undefined,
  exhibition_type_id: task.exhibition_type_id || undefined,
  manager_note: task.final_changes.manager_note || "",
  reply_handoff_note: task.final_changes.reply_handoff_note || "",
  equipment_changes:
    task.final_changes.equipment_changes.length > 0
      ? task.final_changes.equipment_changes.map((change) => {
        const matchedContextGroup = findMatchingContextGroup(change, task.context_groups || []);
        const suggestedEquipmentGroupId = resolveEquipmentGroupId(
          change.suggested_equipment_group_id || change.equipment_group_id,
          change.equipment_id,
          equipmentIdToGroupIdMap
        );
        const resolvedEquipmentGroupId = resolveEquipmentGroupId(
          change.equipment_group_id || suggestedEquipmentGroupId,
          change.equipment_id,
          equipmentIdToGroupIdMap
        );

        return {
          ...change,
          event_name: change.event_name || matchedContextGroup?.event_name || task.event_name || "",
          company_name: change.company_name || matchedContextGroup?.company_name || task.company_name || "",
          stand_number: change.stand_number || matchedContextGroup?.stand_number || task.stand_number || "",
          color: localizeCommunicationLookupValue(change.color) || null,
          equipment_group_id: resolvedEquipmentGroupId,
          suggested_equipment_group_id: suggestedEquipmentGroupId,
          suggestion_status:
            suggestedEquipmentGroupId
              ? change.suggestion_status || "pending"
              : change.suggestion_status || undefined,
        };
      })
      : [ createEmptyEquipmentChange(task.id, task.stand_number || undefined) ],
  stand_updates: {
    flooring_id: task.final_changes.stand_updates.flooring_id || undefined,
    flooring_color:
      localizeCommunicationLookupValue(task.final_changes.stand_updates.flooring_color) || "",
  },
  questions: task.questions.length > 0 ? task.questions : [],
});

const getInitialStep = (task: TCommunicationTask): number => {
  if (
    task.status === CommunicationConst.statuses.APPROVED
    || task.status === CommunicationConst.statuses.DISMISSED
  ) {
    return 3;
  }

  if (task.context_groups?.length) {
    return areContextGroupsComplete(task.context_groups) ? 2 : 1;
  }

  const hasStandMatch = Boolean(
    task.stand_number?.trim() || normalizeStandNumbers(task.stand_numbers).length
  );

  return task.event_name?.trim() && task.company_name?.trim() && hasStandMatch ? 2 : 1;
};

export const getDrawerStartStep = (
  task: TCommunicationTask,
  isAuditView?: boolean
): number => {
  if (isAuditView) {
    return getInitialStep(task);
  }

  return 1;
};

export const getInitialUnlockedStep = (
  task: TCommunicationTask,
  isAuditView?: boolean
): TCommunicationTaskStep["id"] => {
  if (isAuditView || task.status === CommunicationConst.statuses.APPROVED) {
    return 3;
  }

  return getInitialStep(task) as TCommunicationTaskStep["id"];
};

export const buildFormValueSnapshot = (
  task: TCommunicationTask,
  rawValues: Partial<TCommunicationTaskFormValues>,
  resolvedValues: {
    event_name: string;
    company_name: string;
    stand_number: string;
    stand_numbers: string[];
    context_groups: TCommunicationContextGroup[];
    equipment_changes: TCommunicationTaskFormValues["equipment_changes"];
    stand_updates: TCommunicationTaskFormValues["stand_updates"];
    questions: TCommunicationTaskFormValues["questions"];
  }
): TCommunicationTaskFormValues => ({
  event_name: resolvedValues.event_name,
  company_name: resolvedValues.company_name,
  stand_number: resolvedValues.stand_number,
  stand_numbers: resolvedValues.stand_numbers,
  context_groups: resolvedValues.context_groups,
  organizer_id: rawValues.organizer_id || task.organizer_id || undefined,
  exhibition_type_id: rawValues.exhibition_type_id || task.exhibition_type_id || undefined,
  manager_note:
    typeof rawValues.manager_note === "string"
      ? rawValues.manager_note
      : task.final_changes.manager_note || "",
  reply_handoff_note:
    typeof rawValues.reply_handoff_note === "string"
      ? rawValues.reply_handoff_note
      : task.final_changes.reply_handoff_note || "",
  equipment_changes: resolvedValues.equipment_changes,
  stand_updates: resolvedValues.stand_updates,
  questions: resolvedValues.questions,
});

export const normalizeTaskFormSnapshot = (
  task: TCommunicationTask,
  rawValues: Partial<TCommunicationTaskFormValues>,
  equipmentIdToGroupIdMap: Map<string, string>
): TCommunicationTaskFormValues => {
  const fallbackValues = mapTaskToFormValues(task, equipmentIdToGroupIdMap);
  const sourceStandNumbers =
    Array.isArray(rawValues.stand_numbers) && rawValues.stand_numbers.length > 0
      ? rawValues.stand_numbers.map((value) => (typeof value === "string" ? value : ""))
      : fallbackValues.stand_numbers;
  const normalizedStandNumbers = normalizeStandNumbers(sourceStandNumbers);
  const primaryStandNumber =
    normalizedStandNumbers[0]
    || (typeof rawValues.stand_number === "string" ? rawValues.stand_number.trim() : "")
    || fallbackValues.stand_number
    || "";
  const normalizedContextGroups = normalizeContextGroups(
    Array.isArray(rawValues.context_groups) && rawValues.context_groups.length > 0
      ? rawValues.context_groups
      : fallbackValues.context_groups,
    task.id
  );
  const primaryContextGroup = getPrimaryContextGroup(normalizedContextGroups);

  return buildFormValueSnapshot(task, rawValues, {
    event_name: primaryContextGroup?.event_name || fallbackValues.event_name,
    company_name: primaryContextGroup?.company_name || fallbackValues.company_name,
    stand_number: primaryContextGroup?.stand_number || primaryStandNumber,
    stand_numbers: normalizeStandNumbers([
      ...normalizedContextGroups.map((group) => group.stand_number),
      ...normalizedStandNumbers,
    ]),
    context_groups: normalizedContextGroups,
    equipment_changes: rawValues.equipment_changes || fallbackValues.equipment_changes,
    stand_updates: rawValues.stand_updates || fallbackValues.stand_updates,
    questions: rawValues.questions || fallbackValues.questions,
  });
};
